import {
  Avatar,
  Box,
  Group,
  HoverCard,
  Menu,
  NavLink,
  Stack,
  Text,
  ThemeIcon
} from "@mantine/core";
import { useMsal } from "@azure/msal-react";
import { IconDotsVertical, IconLogout } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import {
  type CustomClaim,
  CustomClaimTypes,
  EntraAppRoleGlobalAdministrator,
  isSparkRoleKey,
  MSFT_ROLE_CLAIM_TYPE,
  SparkRoleCompactDisplayName
} from "@/models";
import { getUserClaimsQuery } from "@/api/userClaims/getUserClaims";
import { getCommunitiesForSelectionQuery, useApi } from "@/api";

const RoleDisplayPriority = new Map<string, number>([
  [CustomClaimTypes.scopedRole, 0],
  [CustomClaimTypes.forCommunity, 1],
  [MSFT_ROLE_CLAIM_TYPE, 2]
]);

const filterAndSortClaimsForSpark = (claims: CustomClaim[]): CustomClaim[] => {
  const roleClaims = claims.filter((claim) =>
    [CustomClaimTypes.scopedRole, MSFT_ROLE_CLAIM_TYPE].includes(claim.type)
  );
  roleClaims.sort(
    (a, b) =>
      (RoleDisplayPriority.get(a.type) ?? 0) - // a and b will always have a type in the RoleDisplayPriority map at
      (RoleDisplayPriority.get(b.type) ?? 0) // this point. The nullish operator just satisfies the linter
  );

  return roleClaims;
};

export interface UserMenuComponentProps {
  navIsCollapsed?: boolean;
}
export default function UserMenuComponent({
  navIsCollapsed
}: UserMenuComponentProps) {
  const { accounts, instance } = useMsal();
  const { secureAxios } = useApi();
  const displayName = accounts[0]?.name;
  const loginId = accounts[0]?.username;
  const communities = useQuery(
    getCommunitiesForSelectionQuery(secureAxios)
  ).data;
  const userClaim = useQuery(getUserClaimsQuery(secureAxios)).data;
  const sortedClaims = filterAndSortClaimsForSpark(userClaim?.claims ?? []);
  let displayClaim =
    sortedClaims.length > 0 ? sortedClaims[0].value : "No Role Assigned";
  if (displayClaim === EntraAppRoleGlobalAdministrator) {
    displayClaim = "Global Administrator";
  } else if (isSparkRoleKey(displayClaim)) {
    displayClaim = SparkRoleCompactDisplayName[displayClaim];
  }
  const secondaryClaims = [] as string[];
  sortedClaims
    .filter((c) => c.type === CustomClaimTypes.scopedRole)
    .forEach((c, i) => {
      const claimParts = c.value.split("|");
      const roleKey = claimParts.length === 2 ? claimParts[0] : "";
      const communityId = claimParts.length === 2 ? claimParts[1] : "";
      const community = communities?.find(
        (com) => communityId != "" && com.id === parseInt(communityId)
      );
      if (community) {
        const roleLabel = isSparkRoleKey(roleKey)
          ? SparkRoleCompactDisplayName[roleKey]
          : roleKey;
        const claim = `${roleLabel}, ${community.name} `;
        if (i === 0) {
          displayClaim = claim;
        } else {
          secondaryClaims.push(claim);
        }
      }
    });

  return (
    <Group gap={8} mr={10}>
      <Avatar color="navy.9" name={displayName} radius="xl" title={loginId} />
      <Box visibleFrom={navIsCollapsed ? "xs" : "sm"}>
        <Text title={loginId}>{displayName ?? "Not signed in"}</Text>
        {secondaryClaims.length > 0 ? (
          <HoverCard>
            <HoverCard.Target>
              <Text size="xs" c="gray.7">
                {`${displayClaim} (+${secondaryClaims.length})`}
              </Text>
            </HoverCard.Target>
            <HoverCard.Dropdown>
              <Stack>
                {secondaryClaims.map((claim) => (
                  <Text key={`${claim}`} size="xs" c="gray.7">
                    {claim}
                  </Text>
                ))}
              </Stack>
            </HoverCard.Dropdown>
          </HoverCard>
        ) : (
          <Text size="xs" c="gray.7">
            {displayClaim}
          </Text>
        )}
      </Box>

      <Menu width={160} withArrow>
        <Menu.Target>
          <ThemeIcon c="optimaBlue.9" variant="white" radius="xl">
            <IconDotsVertical />
          </ThemeIcon>
        </Menu.Target>
        <Menu.Dropdown>
          {accounts[0] && (
            <NavLink
              label="Log Out"
              onClick={() =>
                void instance.logoutRedirect({
                  account: accounts[0],
                  postLogoutRedirectUri: "https://optimaliving.ca"
                })
              }
              leftSection={<IconLogout size={25} />}
              c="gray.7"
              variant="subtle"
            />
          )}
        </Menu.Dropdown>
      </Menu>
    </Group>
  );
}

UserMenuComponent.filterAndSortClaimsForSpark = filterAndSortClaimsForSpark;
